import React, { Suspense } from "react";
import styled from "styled-components";
import API_URL from "./config";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  Navigate,
  useLocation,
} from "react-router-dom";

const JobsList = React.lazy(() => import("./Jobs/JobsList"));
const LoginPage = React.lazy(() => import("./Login/LoginPage"));
const LinkedInAuth = React.lazy(() => import("./Login/LinkedInAuth"));
const ProtectedRoute = React.lazy(() => import("./ProtectRoute"));
const NewJobForm = React.lazy(() => import("./Jobs/NewJobForm"));
const JobDetails = React.lazy(() => import("./Jobs/JobDetails"));
const NavBar = React.lazy(() => import("./NavBar"));
const EditForm = React.lazy(() => import("./Jobs/EditForm"));
const TeamsList = React.lazy(() => import("./Teams/TeamsList"));
const AcceptInvite = React.lazy(() => import("./Teams/AcceptInvite"));
const AccountDisabled = React.lazy(() => import("./Login/AccountDisabled"));
const ProfileComp = React.lazy(() => import("./Login/Profile"));
const ResetPasswordComp = React.lazy(() => import("./Login/ResetPassword"));
const RewardsComponent = React.lazy(() => import("./Rewards/Rewards"));
const AccountPage = React.lazy(() => import("./Account/AccountPage"));
const Footer = React.lazy(() => import("./Components/Footer"));
const BillingPage = React.lazy(() => import("./Billing/BillingPage"));
const PaymentsPage = React.lazy(() => import("./Billing/PaymentsPage"));
const BillingHistoryPage = React.lazy(() => import("./Billing/BillingHistoryPage"));
const UpgradePage = React.lazy(() => import("./Billing/UpgradePage"));
const PromoteJobPage = React.lazy(() => import("./Jobs/PromoteJobPage"));
const PromotionsQueue = React.lazy(() => import("./Jobs/PromotionsQueue"));
const JobPostReminderUnsubscribe = React.lazy(() => import("./Jobs/JobPostReminderUnsubscribe"));
const JobLinkReminderUnsubscribe = React.lazy(() => import("./Jobs/JobLinkReminderUnsubscribe"));
const JobReviewReminderUnsubscribe = React.lazy(() => import("./Jobs/JobReviewReminderUnsubscribe"));
const PublicCandidateVideoQA = React.lazy(() => import("./Jobs/PublicCandidateVideoQA"));
const PublicInterviewSchedule = React.lazy(() => import("./Jobs/PublicInterviewSchedule"));
const InterviewAvailability = React.lazy(() => import("./InterviewAvailability"));
const ZoomHelp = React.lazy(() => import("./ZoomHelp"));
const BullhornLandingPage = React.lazy(() => import("./Landing/BullhornLandingPage"));
const CSPLandingPage      = React.lazy(() => import("./Landing/CSPLandingPage"));
const EmployerUpload      = React.lazy(() => import("./Jobs/EmployerUpload"));
const EmployerCreateAccount = React.lazy(() => import("./Jobs/EmployerCreateAccount"));

const SignUp = React.lazy(() => import("./Login/SignUp"));
const ImpersonatePage = React.lazy(() => import("./Login/ImpersonatePage"));

// Lazy-load reCAPTCHA provider so its script loads after first paint
const GoogleReCaptchaProvider = React.lazy(() =>
  import("react-google-recaptcha-v3").then((m) => ({ default: m.GoogleReCaptchaProvider }))
);

// Skip-to-content link (WCAG 2.4.1 Bypass Blocks) — first focusable element on
// the page; visually hidden until focused (styles in index.css).
const SkipLink = () => (
  <a className="cp-skip-link" href="#main-content">Skip to main content</a>
);

// Per-route document title (WCAG 2.4.2 Page Titled) — the SPA otherwise keeps the
// one static <title>. First matching pattern wins, so order specific → generic.
const ROUTE_TITLES = [
  [/\/login/, "Sign In"],
  [/\/sign-up/, "Sign Up"],
  [/\/reset-password/, "Reset Password"],
  [/\/account-disabled/, "Account Disabled"],
  [/\/jobs\/create-new-job/, "Post a Job"],
  [/\/jobs\/[^/]+\/edit/, "Edit Job"],
  [/\/jobs\/[^/]+\/promote/, "Promote Job"],
  [/_unsubscribe/, "Unsubscribe"],
  [/\/jobs\/[^/]+$/, "Job Details"],
  [/\/jobs/, "Jobs"],
  [/\/teams\/accept-invite/, "Accept Team Invite"],
  [/\/teams/, "Teams"],
  [/\/profile/, "Profile"],
  [/\/account/, "Account"],
  [/\/rewards/, "Rewards"],
  [/\/billing-history/, "Billing History"],
  [/\/billing/, "Billing"],
  [/\/payments/, "Payments"],
  [/\/upgrade/, "Upgrade Plan"],
  [/\/public\/video-qa/, "Video Interview"],
  [/\/employer\/upload/, "Upload Candidates"],
  [/\/employer\/create-account/, "Create Account"],
  [/ai-for-bullhorn/, "AI for Bullhorn Teams"],
  [/ai-for-csp/, "AI for CSP Members"],
];

const RouteTitle = () => {
  const { pathname } = useLocation();
  React.useEffect(() => {
    const base = "CrowdPlat Recruiter Portal";
    const hit = ROUTE_TITLES.find(([re]) => re.test(pathname));
    document.title = hit ? `${hit[1]} — ${base}` : base;
  }, [pathname]);
  return null;
};

// ✅ Layouts (ADD HERE)

const AdminLayout = ({ children }) => (
  <AppContainer>
    <SkipLink />
    <NavBar />
    <MainContent id="main-content" tabIndex={-1}>{children}</MainContent>
    <Footer />
  </AppContainer>
);

const PublicLayout = ({ children }) => (
  <AppContainer>
    <SkipLink />
    <MainContent id="main-content" tabIndex={-1}>{children}</MainContent>
  </AppContainer>
);

const AppContent = () => {
  return (
    <>
      <RouteTitle />
      <Routes>
      {/* ✅ MARKETING LANDING PAGES (no navbar, no footer, no banner) */}
      <Route path="/ai-for-bullhorn-teams" element={<BullhornLandingPage />} />
      <Route path="/ai-for-csp-members"   element={<CSPLandingPage />} />

      {/* ✅ PUBLIC C2C-EMPLOYER UPLOAD (no login, own blue nav/footer) */}
      <Route path="/employer/upload" element={<EmployerUpload />} />
      {/* ✅ PUBLIC "Create account" from the verification email (no login) */}
      <Route path="/employer/create-account" element={<EmployerCreateAccount />} />

      {/* ✅ CLEAN PUBLIC PAGE (no navbar, no footer, no banner) */}
      <Route
        path="/public/video-qa/:jobId/:resumeId"
        element={
          <PublicLayout>
            <PublicCandidateVideoQA />
          </PublicLayout>
        }
      />
      <Route
        path="/public/interview/:jobId/:resumeId"
        element={
          <PublicLayout>
            <PublicInterviewSchedule />
          </PublicLayout>
        }
      />
     <Route
      path="/recruitment-admin/teams/accept-invite/:token"
      element={<AcceptInvite />}
    />  
      {/* ✅ ADMIN APP (navbar exists ONLY here) */}
      <Route
        path="/recruitment-admin/*"
        element={
          <AdminLayout>
            <Routes>
                <Route path="/login" element={<LoginPage />} />
                {/* Public Zoom integration help — the Documentation URL for the
                    Zoom Marketplace listing (no login). */}
                <Route path="/zoom-help" element={<ZoomHelp />} />
                <Route path="/sign-up" element={<BodyWrap><SignUp /></BodyWrap>} />
                <Route path="/linkedin-auth" element={<LinkedInAuth />} />
                <Route path="/impersonate" element={<ImpersonatePage />} />
                <Route path="/account-disabled" element={<BodyWrap><AccountDisabled /></BodyWrap>} />
                <Route
                  path="/jobs/job_post_reminder_unsubscribe"
                  element={
                    <BodyWrap>
                      <JobPostReminderUnsubscribe />
                    </BodyWrap>
                  }
                />
                <Route
                  path="/jobs/job_link_reminder_unsubscribe"
                  element={
                    <BodyWrap>
                      <JobLinkReminderUnsubscribe />
                    </BodyWrap>
                  }
                />
                <Route
                  path="/jobs/job_review_reminder_unsubscribe"
                  element={
                    <BodyWrap>
                      <JobReviewReminderUnsubscribe />
                    </BodyWrap>
                  }
                />
                <Route
                  path="/jobs"
                  element={
                    <ProtectedRoute>
                      <BodyWrap>
                        <JobsList />
                      </BodyWrap>
                    </ProtectedRoute>
                  }
                />
                {/* Legacy DIR job route — now unified into the standard create-new-job form */}
                <Route
                  path="/jobs/create-new-dir-job"
                  element={<Navigate to="/recruitment-admin/jobs/create-new-job" replace />}
                />
                <Route
                  path="/jobs/create-new-job"
                  element={
                    <ProtectedRoute>
                      <BodyWrap>
                        <NewJobForm />
                      </BodyWrap>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/jobs/:jobId"
                  element={
                    <ProtectedRoute>
                      <BodyWrap>
                        <JobDetails />
                      </BodyWrap>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/jobs/:jobId/edit"
                  element={
                    <ProtectedRoute>
                      <BodyWrap>
                        <EditForm />
                      </BodyWrap>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/jobs/:jobId/edit-dir-job"
                  element={<Navigate to="../edit" replace />}
                />
                <Route
                  path="/jobs/:jobId/promote"
                  element={
                    <ProtectedRoute>
                      <BodyWrap>
                        <PromoteJobPage />
                      </BodyWrap>
                    </ProtectedRoute>
                  }
                />
                {/* Internal-only fulfillment queue (server-gated by account_type). */}
                <Route
                  path="/promotions"
                  element={
                    <ProtectedRoute>
                      <BodyWrap>
                        <PromotionsQueue />
                      </BodyWrap>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/teams"
                  element={
                    <ProtectedRoute>
                      <BodyWrap>
                        <TeamsList />
                      </BodyWrap>
                    </ProtectedRoute>
                  }
                />             
                <Route
                  path="/profile"
                  element={
                    <ProtectedRoute>
                      <BodyWrap>
                        <ProfileComp />
                      </BodyWrap>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/interview-availability"
                  element={
                    <ProtectedRoute>
                      <BodyWrap>
                        <InterviewAvailability />
                      </BodyWrap>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/account"
                  element={
                    <ProtectedRoute>
                      <BodyWrap>
                        <AccountPage />
                      </BodyWrap>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/rewards"
                  element={
                    <ProtectedRoute>
                      <BodyWrap>
                        <RewardsComponent />
                      </BodyWrap>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/reset-password/:token"
                  element={
                    <BodyWrap><ResetPasswordComp /></BodyWrap>
                  }
                />
                <Route
                  path="/billing"
                  element={
                    <ProtectedRoute>
                      <BodyWrap>
                        <BillingPage />
                      </BodyWrap>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/payments"
                  element={
                    <ProtectedRoute>
                      <BodyWrap>
                        <PaymentsPage />
                      </BodyWrap>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/billing-history"
                  element={
                    <ProtectedRoute>
                      <BodyWrap>
                        <BillingHistoryPage />
                      </BodyWrap>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/upgrade"
                  element={
                    <ProtectedRoute>
                      <BodyWrap>
                        <UpgradePage />
                      </BodyWrap>
                    </ProtectedRoute>
                  }
                />
            </Routes>
          </AdminLayout>
        }
      />
    </Routes>
    </>
  );
};

const App = () => {
  const [siteKey, setSiteKey] = React.useState(null);

  // Claim this window's experience (_hire) so cross-app links reuse ONE window
  // per experience instead of opening duplicates. Matches the www CustomerNavBar
  // (_home) / FreelancerNavBar (_work) claimers and the AI Hire static pages'
  // head claimer. Without this, an AI Hire tab opened directly stays unnamed, so
  // a later "Hiring" click from the home tab can't find it and spawns a duplicate.
  React.useEffect(() => { try { window.name = "_hire"; } catch (e) {} }, []);

  React.useEffect(() => {
    fetch(`${API_URL}/api-recruitment-admin/config`)
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then((d) => setSiteKey(String(d?.recaptchaSiteKey || "").trim()))
      .catch((e) => {
        console.error("Failed to load reCAPTCHA site key:", e);
        setSiteKey(""); // prevent infinite blank screen
      });
  }, []);

  // Don’t render blank forever — show something while debugging
  if (siteKey === null) return <div style={{ padding: 24 }}>Loading…</div>;

  // If missing key, you can either block or allow app to render without reCAPTCHA
  if (!siteKey) return <div style={{ padding: 24 }}>Missing reCAPTCHA site key</div>;

  return (
    <Suspense fallback={null}>
      <GoogleReCaptchaProvider reCaptchaKey={siteKey}>
        <Router>
          <AppContent />
        </Router>
      </GoogleReCaptchaProvider>
    </Suspense>
  );
};

const AppContainer = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
`;

const MainContent = styled.main`
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const BodyWrap = styled.article`
  background-color: #faf9f6;
  padding: 40px;
  flex: 1;
  min-height: auto;

  @media (max-width: 768px) {
    padding: 20px;
  }

  @media (max-width: 480px) {
    padding: 15px;
  }
`;

export default App;
